<script lang="ts">
  import {
    AlertTriangle,
    Activity,
    BarChart3,
    Check,
    ChevronDown,
    ClipboardList,
    Clock,
    Copy,
    GitCompare,
    Hammer,
    Loader2,
    MoreHorizontal,
    Pencil,
    Radio,
    TrendingDown,
    TrendingUp,
    Wrench,
    X,
    Zap,
  } from 'lucide-svelte';
  import { push } from 'svelte-spa-router';
  import type { LiveSessionCard } from '$lib/api/liveMonitor';
  import { type ParityStatus, patchSessionTitle } from '$lib/api/sessions';
  import {
    fetchSessionConfig,
    fetchSessionCompare,
    fetchSessionSnapshot,
    fetchSlippageAudit,
    type CompareRenkoContext,
    type CompareTrade,
    type CompareTradePair,
    type ConfigSnapshot,
    type SessionCompare,
    type SessionSnapshot,
    type SlippageAudit,
  } from '$lib/api/tools';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import { Card } from '$lib/components/ui/card';
  import { Dialog, DialogContent, DialogDescription, DialogTitle } from '$lib/components/ui/dialog';
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
  } from '$lib/components/ui/dropdown-menu';
  import { Tooltip, TooltipContent, TooltipTrigger } from '$lib/components/ui/tooltip';
  import { clock } from '$lib/stores/clock.svelte';
  import { liveLookStore } from '$lib/stores/liveLook.svelte';
  import {
    type UnrealizedPnLReason,
    livePriceStore,
    unrealizedPnL,
    unrealizedPnLReason,
  } from '$lib/stores/livePrice.svelte';
  import { copyText, flashCopied } from '$lib/utils/clipboard';
  import CopyableSessionId from './CopyableSessionId.svelte';
  import ParityWidget from './ParityWidget.svelte';
  import SessionDetailPanel from './SessionDetailPanel.svelte';
  import Sparkline from './Sparkline.svelte';

  interface Props {
    session: LiveSessionCard;
    mode: 'live' | 'developer';
    livePrice: number | null;
    expanded: boolean;
    onToggle: (sessionId: string) => void;
    parityStatus?: ParityStatus | null;
    renkoExists?: boolean;
    compareCandidates?: LiveSessionCard[];
  }

  let {
    session,
    mode,
    livePrice,
    expanded,
    onToggle,
    parityStatus = null,
    renkoExists = false,
    compareCandidates = [],
  }: Props = $props();

  // ----- Local state -----
  let editingTitle = $state(false);
  let titleDraft = $state('');
  let savingTitle = $state(false);
  let toolAction = $state<'snapshot' | 'config' | 'slippage' | 'compare' | null>(null);
  let copiedTool = $state<'snapshot' | 'config' | 'audit' | 'compare' | null>(null);
  let toolError = $state<string | null>(null);
  let slippageOpen = $state(false);
  let slippageAudit = $state<SlippageAudit | null>(null);
  let compareOpen = $state(false);
  let compareTarget = $state('');
  let compareResult = $state<SessionCompare | null>(null);
  let compareError = $state<string | null>(null);
  let expandedCompareTradeIndexes = $state<Set<number>>(new Set());
  let compareCandidatesForMenu = $derived.by((): LiveSessionCard[] => {
    return compareCandidates
      .filter((candidate) => candidate.sessionId !== session.sessionId)
      .sort((a, b) => {
        const symbol = a.symbol.localeCompare(b.symbol);
        if (symbol !== 0) return symbol;
        const window = sessionWindowLabel(a).localeCompare(sessionWindowLabel(b));
        if (window !== 0) return window;
        return b.sessionId.localeCompare(a.sessionId);
      });
  });
  let selectedCompareCandidate = $derived.by((): LiveSessionCard | null => {
    return (
      compareCandidatesForMenu.find((candidate) => candidate.sessionId === compareTarget) ?? null
    );
  });
  let equityValue = $derived.by((): number | null => {
    const lastPoint = session.recentClosedPnL[session.recentClosedPnL.length - 1];
    return lastPoint?.cumulativePnL ?? null;
  });

  function startEditTitle(): void {
    titleDraft = session.customTitle ?? '';
    editingTitle = true;
  }

  function cancelEditTitle(): void {
    editingTitle = false;
    titleDraft = '';
  }

  async function saveTitle(): Promise<void> {
    if (savingTitle) return;
    savingTitle = true;
    try {
      await patchSessionTitle(session.sessionId, titleDraft.trim());
      // Optimistic update visual; el proximo poll refresca el dato real.
      session.customTitle = titleDraft.trim() || null;
      editingTitle = false;
    } catch (err) {
      console.error('[live-monitor] patchSessionTitle fallo', err);
    } finally {
      savingTitle = false;
    }
  }

  function onTitleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditTitle();
    }
  }

  // ----- Live P&L unrealized -----
  // Suma de todos los open trades. Si CUALQUIERA tiene valuePerPoint null,
  // ese trade reporta null y la suma queda null (UI muestra etiqueta segun
  // motivo). Audit v1 §6 fix: NO default a 20.
  //
  // Devuelve { value, reason }: value es el pnl total cuando se puede,
  // reason es el primer motivo de falla (no-vpp > waiting-price > no-trade)
  // que la UI usa para mostrar una etiqueta accionable en lugar de "N/A".
  let unrealizedStatus = $derived.by((): {
    value: number | null;
    reason: UnrealizedPnLReason;
  } => {
    if (session.openTrades.length === 0) return { value: null, reason: null };
    let sum = 0;
    let firstReason: UnrealizedPnLReason = null;
    for (const t of session.openTrades) {
      const input = {
        side: t.side,
        entryPrice: t.entryPrice,
        quantity: t.quantity,
        valuePerPoint: t.valuePerPoint,
        livePrice,
      };
      const pnl = unrealizedPnL(input);
      if (pnl == null) {
        // Priorizamos no-vpp (sistemico) sobre waiting-price (transitorio).
        const r = unrealizedPnLReason(input);
        if (firstReason == null || (firstReason === 'waiting-price' && r === 'no-vpp')) {
          firstReason = r;
        }
        continue;
      }
      sum += pnl;
    }
    if (firstReason != null) return { value: null, reason: firstReason };
    return { value: sum, reason: null };
  });
  let unrealizedTotal = $derived(unrealizedStatus.value);
  let unrealizedReason = $derived(unrealizedStatus.reason);

  // Etiqueta para mostrar cuando unrealizedTotal == null. Diferencia el
  // motivo (waiting-price | no-vpp | no-trade) para que el operador sepa
  // si esperar, si avisar al equipo, o si el dato simplemente no esta.
  // upstream-down sobreescribe waiting-price cuando el WS no esta arriba.
  let upstreamDown = $derived(
    livePriceStore.status !== 'open' || livePriceStore.upstreamStatus !== 'open',
  );
  let unrealizedLabel = $derived.by((): string => {
    if (unrealizedReason === 'no-vpp') return 'No vpp';
    if (unrealizedReason === 'no-trade') return 'No data';
    if (unrealizedReason === 'waiting-price') {
      return upstreamDown ? 'Connecting…' : 'Waiting tick';
    }
    return 'N/A';
  });
  let unrealizedLabelTitle = $derived.by((): string => {
    if (unrealizedReason === 'no-vpp') {
      return 'valuePerPoint missing on this trade - cannot compute';
    }
    if (unrealizedReason === 'no-trade') return 'Missing trade fields (side/entry/qty)';
    if (unrealizedReason === 'waiting-price') {
      return upstreamDown
        ? 'Price feed connecting - try again in a few seconds'
        : 'Connected - waiting for next price tick';
    }
    return '';
  });

  // ----- Formatters -----
  // Convencion: el signo (-, +) va SIEMPRE antes del `$`, no entre `$` y el numero.
  //   +$95.00 (positivo con prefijo opcional)
  //   -$95.00 (negativo)
  //    $0.00  (sin sign)
  function fmtPnL(n: number | null): string {
    if (n == null) return 'N/A';
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtMoney(n: number | null): string {
    if (n == null) return '—';
    if (n < 0) return `-$${Math.abs(n).toFixed(2)}`;
    return `$${n.toFixed(2)}`;
  }

  function fmtEquity(n: number | null): string {
    if (n == null) return '';
    if (n === 0) return '$0';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(0)}`;
  }

  function fmtPF(pf: number | null, trades: number): string {
    if (trades === 0) return '—';
    if (pf === null) return '∞';
    if (!Number.isFinite(pf)) return '—';
    return pf.toFixed(2);
  }

  function fmtAccount(name: string | null): string {
    if (!name) return '—';
    const trimmed = name.trim();
    if (/^sim\b/i.test(trimmed)) return 'SIM';
    return trimmed;
  }

  function fmtPrice(n: number | null): string {
    if (n == null) return '—';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function fmtDateTime(t: string | null): string {
    if (!t) return '—';
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  function cleanOperator(op: string): string {
    return op.replace('@rabbyt.ai', '');
  }

  function shortSessionId(id: string): string {
    if (id.length <= 14) return id;
    return `${id.slice(0, 8)}...${id.slice(-4)}`;
  }

  function shortValue(value: string | null): string {
    if (!value) return 'N/A';
    if (value.length <= 16) return value;
    return `${value.slice(0, 8)}...${value.slice(-5)}`;
  }

  function sessionWindowLabel(candidate: LiveSessionCard): string {
    if (!candidate.dailyStartTime && !candidate.dailyEndTime) return 'No window';
    return `${candidate.dailyStartTime ?? '?'}-${candidate.dailyEndTime ?? '?'}`;
  }

  function sessionTimespanLabel(candidate: LiveSessionCard): string {
    const value = candidate.config?.timeSpan;
    return typeof value === 'string' && value.length > 0 ? value : 'N/A';
  }

  function sessionStartLabel(candidate: LiveSessionCard): string {
    if (!candidate.startTime) return 'N/A';
    return fmtDateTime(candidate.startTime);
  }

  function selectCompareCandidate(candidate: LiveSessionCard): void {
    compareTarget = candidate.sessionId;
    compareResult = null;
    compareError = null;
    expandedCompareTradeIndexes = new Set();
  }

  function fmtField(value: string | number | boolean | null | undefined): string {
    if (value == null || value === '') return 'N/A';
    return String(value);
  }

  function fmtTimeDiff(ms: number | null): string {
    if (ms == null) return 'N/A';
    if (ms === 0) return '0s';
    const sign = ms > 0 ? '+' : '-';
    const abs = Math.abs(ms);
    if (abs < 1000) return `${sign}${abs}ms`;
    if (abs < 60_000) return `${sign}${(abs / 1000).toFixed(1)}s`;
    const minutes = abs / 60_000;
    if (minutes < 60) return `${sign}${minutes.toFixed(1)}m`;
    return `${sign}${(minutes / 60).toFixed(1)}h`;
  }

  function fmtSlippagePts(n: number | null): string {
    if (n == null) return 'N/A';
    if (n === 0) return '0.00 pts';
    const sign = n > 0 ? '+' : '-';
    return `${sign}${Math.abs(n).toFixed(2)} pts`;
  }

  function entrySlippagePts(trade: CompareTrade): number | null {
    if (trade.side == null || trade.entryTriggerPrice == null || trade.entryPrice == null) {
      return null;
    }
    return trade.side === 'long'
      ? trade.entryTriggerPrice - trade.entryPrice
      : trade.entryPrice - trade.entryTriggerPrice;
  }

  function exitSlippagePts(trade: CompareTrade): number | null {
    if (trade.side == null || trade.exitTriggerPrice == null || trade.exitPrice == null) {
      return null;
    }
    return trade.side === 'long'
      ? trade.exitPrice - trade.exitTriggerPrice
      : trade.exitTriggerPrice - trade.exitPrice;
  }

  function fmtSlippageWithRaw(computed: number | null, raw: number | null): string {
    if (computed == null) return raw == null ? 'N/A' : `raw ${fmtField(raw)}`;
    const rendered = fmtSlippagePts(computed);
    if (raw != null && Math.abs(raw - computed) > 0.000001) {
      return `${rendered} (raw ${fmtField(raw)})`;
    }
    return rendered;
  }

  function fmtPairStatus(pair: CompareTradePair): string {
    if (pair.pairStatus === 'matched') return 'paired by time';
    if (pair.pairStatus === 'matched_by_index') return 'paired by order';
    if (pair.pairStatus === 'base_only') return 'base only';
    return 'other only';
  }

  function tradeMismatchTone(pair: CompareTradePair): string {
    if (
      pair.pairStatus !== 'matched' ||
      pair.sideMismatch ||
      pair.statusMismatch ||
      !pair.base ||
      !pair.other
    ) {
      return 'text-warning';
    }
    return 'text-muted-foreground';
  }

  function toggleCompareTrade(index: number): void {
    const next = new Set(expandedCompareTradeIndexes);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    expandedCompareTradeIndexes = next;
  }

  function tradeHeadline(trade: CompareTrade | null): string {
    if (!trade) return 'Missing';
    return `${fmtField(trade.side)} x ${fmtField(trade.quantity)}`;
  }

  function tradeSubline(trade: CompareTrade | null): string {
    if (!trade) return 'No trade at this index';
    return `${fmtDateTime(trade.entryTime)} -> ${fmtDateTime(trade.exitTime)}`;
  }

  function renkoLabel(block: CompareRenkoContext | null): string {
    if (!block) return 'No block';
    return `#${block.index} ${fmtField(block.direction)} @ ${fmtPrice(block.close)} (${fmtField(block.time ?? block.startTime)})`;
  }

  function renkoShortLabel(block: CompareRenkoContext | null): string {
    if (!block) return 'No block';
    return `ctx #${block.index} ${fmtField(block.direction)}`;
  }

  function derivedOutcome(trade: CompareTrade): string | null {
    if (trade.outcome) return trade.outcome;
    if (trade.pnl > 0) return 'win (derived)';
    if (trade.pnl < 0) return 'loss (derived)';
    if (trade.status === 'closed') return 'flat (derived)';
    return null;
  }

  function tradeDetailRows(trade: CompareTrade | null): Array<[string, string]> {
    if (!trade) return [['Trade', 'Missing']];
    const rows: Array<[string, string]> = [
      ['Trade id', shortValue(trade.tradeId)],
    ];
    if (trade.orderId) rows.push(['Order', fmtField(trade.orderId)]);
    rows.push(
      ['Status', fmtField(trade.status)],
    );
    if (trade.fillTime) rows.push(['Fill time', fmtDateTime(trade.fillTime)]);
    rows.push(
      ['Qty', fmtField(trade.quantity)],
      ['Result', fmtField(derivedOutcome(trade))],
      ['Entry fill', fmtPrice(trade.entryPrice)],
      ['Entry trigger', fmtPrice(trade.entryTriggerPrice)],
      ['Exit fill', fmtPrice(trade.exitPrice)],
      ['Exit trigger', fmtPrice(trade.exitTriggerPrice)],
      ['Entry slip (+fav)', fmtSlippageWithRaw(entrySlippagePts(trade), trade.entrySlippage)],
      ['Exit slip (+fav)', fmtSlippageWithRaw(exitSlippagePts(trade), trade.exitSlippage)],
      ['Close reason', fmtField(trade.closeReason)],
      ['Value/point', fmtField(trade.valuePerPoint)],
    );
    if (trade.stopLoss != null) rows.push(['Stop', fmtPrice(trade.stopLoss)]);
    if (trade.takeProfit != null) rows.push(['Take profit', fmtPrice(trade.takeProfit)]);
    if (trade.contracts != null && trade.contracts !== trade.quantity) {
      rows.push(['Contracts', fmtField(trade.contracts)]);
    }
    if (trade.account) rows.push(['Trade account', fmtField(trade.account)]);
    if (trade.entryRenko) rows.push(['Entry block ctx', renkoLabel(trade.entryRenko)]);
    if (trade.exitRenko) rows.push(['Exit block ctx', renkoLabel(trade.exitRenko)]);
    return rows;
  }

  function fmtJsonValue(value: unknown): string {
    if (value == null) return 'null';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return JSON.stringify(value);
  }

  function formatConfigLines(config: Record<string, unknown>): string[] {
    const entries = Object.entries(config).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) return ['config: N/A'];
    return ['config:', ...entries.map(([key, value]) => `  ${key}: ${fmtJsonValue(value)}`)];
  }

  function formatSnapshot(snapshot: SessionSnapshot): string {
    const lines = [
      'Session Snapshot',
      `sessionId: ${snapshot.sessionId}`,
      `symbol: ${fmtField(snapshot.symbol)}`,
      `account: ${fmtField(snapshot.accountName)}`,
      `operator: ${fmtField(snapshot.operatorId)}`,
      `status: ${snapshot.status}`,
      `start: ${fmtField(snapshot.startTime)}`,
      `end: ${fmtField(snapshot.endTime)}`,
      `realPnL: ${fmtMoney(snapshot.totalPnL)}`,
      `btPnL: ${fmtMoney(snapshot.totalBtPnL)}`,
      `trades: ${snapshot.tradeCount}`,
      `openPositions: ${snapshot.openPositions}`,
      `parity: ${fmtField(snapshot.parityStatus)}`,
      `warmupParity: ${fmtField(snapshot.warmupParityStatus)}`,
      ...formatConfigLines(snapshot.config),
    ];

    if (snapshot.openTrades.length > 0) {
      lines.push('openTrades:');
      for (const t of snapshot.openTrades) {
        lines.push(
          `  - ${fmtField(t.side)} x ${fmtField(t.quantity)} entry ${fmtField(t.entryPrice)} stop ${fmtField(t.stopLoss)} tp ${fmtField(t.takeProfit)} order ${fmtField(t.orderId)}`,
        );
      }
    } else {
      lines.push('openTrades: none');
    }
    return lines.join('\n');
  }

  function formatConfigSnapshot(config: ConfigSnapshot): string {
    return JSON.stringify(
      {
        sessionId: config.sessionId,
        symbol: config.symbol,
        accountName: config.accountName,
        operatorId: config.operatorId,
        dailyStartTime: config.dailyStartTime,
        dailyEndTime: config.dailyEndTime,
        config: config.config,
      },
      null,
      2,
    );
  }

  function formatSlippageAudit(audit: SlippageAudit): string {
    const lines = [
      'Slippage Audit',
      `sessionId: ${audit.sessionId}`,
      `realPnL: ${fmtMoney(audit.totalRealPnL)}`,
      `btPnL: ${fmtMoney(audit.totalBtPnL)}`,
      `diff: ${fmtMoney(audit.pnlDiff)}`,
      `trades: ${audit.tradeCount}`,
      `comparableTrades: ${audit.comparableTradeCount}`,
    ];
    if (audit.warnings.length > 0) {
      lines.push('warnings:');
      for (const warning of audit.warnings) lines.push(`  - ${warning}`);
    }
    if (audit.largestDiffs.length > 0) {
      lines.push('largestDiffs:');
      for (const t of audit.largestDiffs) {
        lines.push(
          `  - ${fmtField(t.entryTime)} ${fmtField(t.side)} x ${fmtField(t.quantity)} real ${fmtMoney(t.realPnL ?? null)} bt ${fmtMoney(t.btPnL ?? null)} diff ${fmtMoney(t.diff ?? null)}`,
        );
      }
    }
    return lines.join('\n');
  }

  function changedConfigDiffs(compare: SessionCompare | null) {
    return compare ? compare.configDiffs.filter((d) => d.status !== 'same') : [];
  }

  function fmtDiff(n: number | null): string {
    if (n == null) return 'N/A';
    if (n === 0) return '0';
    return n > 0 ? `+${n}` : String(n);
  }

  function fmtConfigValue(value: unknown): string {
    if (value == null) return 'N/A';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return JSON.stringify(value);
  }

  function formatCompare(compare: SessionCompare): string {
    const diffs = changedConfigDiffs(compare);
    const notableTradePairs = compare.tradePairs.filter(
      (pair) =>
        pair.pairStatus !== 'matched' ||
        pair.sideMismatch ||
        pair.statusMismatch ||
        !pair.base ||
        !pair.other ||
        (pair.pnlDiff ?? 0) !== 0 ||
        (pair.btPnlDiff ?? 0) !== 0 ||
        (pair.entryRenkoIndexDiff ?? 0) !== 0 ||
        (pair.exitRenkoIndexDiff ?? 0) !== 0,
    );
    const lines = [
      'Session Compare',
      `base: ${compare.sessionId}`,
      `other: ${compare.otherSessionId}`,
      `symbol: ${fmtField(compare.base.symbol)} vs ${fmtField(compare.other.symbol)}`,
      `account: ${fmtField(compare.base.accountName)} vs ${fmtField(compare.other.accountName)}`,
      `realPnLDiff: ${fmtMoney(compare.summaryDiff.totalPnLDiff)}`,
      `btPnLDiff: ${fmtMoney(compare.summaryDiff.totalBtPnLDiff)}`,
      `tradeCountDiff: ${fmtDiff(compare.summaryDiff.tradeCountDiff)}`,
      `openPositionsDiff: ${fmtDiff(compare.summaryDiff.openPositionsDiff)}`,
      `configChanged: ${diffs.length}`,
      `tradePairs: ${compare.tradePairs.length}`,
      `pairing: ${compare.pairing.method} ${compare.pairing.toleranceMs}ms`,
      `pairedByTime: ${compare.pairing.matched}`,
      `pairedByOrder: ${compare.pairing.matchedByIndex}`,
      `baseOnly: ${compare.pairing.baseOnly}`,
      `otherOnly: ${compare.pairing.otherOnly}`,
      `tradePairsWithDiffs: ${notableTradePairs.length}`,
    ];
    if (compare.warnings.length > 0) {
      lines.push('warnings:');
      for (const warning of compare.warnings) lines.push(`  - ${warning}`);
    }
    if (diffs.length > 0) {
      lines.push('configDiffs:');
      for (const diff of diffs) {
        lines.push(
          `  - ${diff.key}: ${fmtConfigValue(diff.base)} -> ${fmtConfigValue(diff.other)}`,
        );
      }
    }
    if (notableTradePairs.length > 0) {
      lines.push('tradeDiffs:');
      for (const pair of notableTradePairs.slice(0, 20)) {
        const baseId = shortValue(pair.base?.tradeId ?? pair.base?.orderId ?? null);
        const otherId = shortValue(pair.other?.tradeId ?? pair.other?.orderId ?? null);
        lines.push(
          `  - #${pair.index} ${fmtPairStatus(pair)}: ${baseId} ${tradeHeadline(pair.base)} ${fmtMoney(pair.base?.pnl ?? null)} vs ${otherId} ${tradeHeadline(pair.other)} ${fmtMoney(pair.other?.pnl ?? null)} diff ${fmtMoney(pair.pnlDiff)} entryDelta ${fmtTimeDiff(pair.entryTimeDiffMs)} entryBlockCtxDiff ${fmtDiff(pair.entryRenkoIndexDiff)}`,
        );
      }
    }
    return lines.join('\n');
  }

  function setToolCopied(kind: 'snapshot' | 'config'): void {
    flashCopied(
      () => (copiedTool = kind),
      () => {
        if (copiedTool === kind) copiedTool = null;
      },
    );
  }

  async function copySnapshot(e?: MouseEvent): Promise<void> {
    e?.stopPropagation();
    if (toolAction) return;
    toolAction = 'snapshot';
    toolError = null;
    try {
      const snapshot = await fetchSessionSnapshot(session.sessionId);
      if (await copyText(formatSnapshot(snapshot))) setToolCopied('snapshot');
      else toolError = 'Clipboard unavailable';
    } catch (err) {
      console.error('[session-tools] snapshot fallo', err);
      toolError = 'Snapshot failed';
    } finally {
      toolAction = null;
    }
  }

  async function copyConfig(e?: MouseEvent): Promise<void> {
    e?.stopPropagation();
    if (toolAction) return;
    toolAction = 'config';
    toolError = null;
    try {
      const config = await fetchSessionConfig(session.sessionId);
      if (await copyText(formatConfigSnapshot(config))) setToolCopied('config');
      else toolError = 'Clipboard unavailable';
    } catch (err) {
      console.error('[session-tools] config fallo', err);
      toolError = 'Config copy failed';
    } finally {
      toolAction = null;
    }
  }

  async function openSlippageAudit(e?: MouseEvent): Promise<void> {
    e?.stopPropagation();
    if (toolAction) return;
    toolAction = 'slippage';
    toolError = null;
    try {
      slippageAudit = await fetchSlippageAudit(session.sessionId);
      slippageOpen = true;
    } catch (err) {
      console.error('[session-tools] slippage audit fallo', err);
      toolError = 'Slippage audit failed';
    } finally {
      toolAction = null;
    }
  }

  function openCompare(e: MouseEvent): void {
    e.stopPropagation();
    compareTarget = '';
    compareError = null;
    compareResult = null;
    expandedCompareTradeIndexes = new Set();
    compareOpen = true;
  }

  async function runCompare(e?: Event): Promise<void> {
    e?.preventDefault();
    const other = compareTarget.trim();
    if (!other || toolAction) return;
    toolAction = 'compare';
    compareError = null;
    expandedCompareTradeIndexes = new Set();
    try {
      compareResult = await fetchSessionCompare(session.sessionId, other);
    } catch (err) {
      console.error('[session-tools] compare fallo', err);
      compareError = 'Compare failed';
      compareResult = null;
    } finally {
      toolAction = null;
    }
  }

  async function copyCurrentAudit(): Promise<void> {
    if (!slippageAudit) return;
    if (await copyText(formatSlippageAudit(slippageAudit))) {
      flashCopied(
        () => (copiedTool = 'audit'),
        () => {
          if (copiedTool === 'audit') copiedTool = null;
        },
      );
    }
  }

  async function copyCurrentCompare(): Promise<void> {
    if (!compareResult) return;
    if (await copyText(formatCompare(compareResult))) {
      flashCopied(
        () => (copiedTool = 'compare'),
        () => {
          if (copiedTool === 'compare') copiedTool = null;
        },
      );
    }
  }

  function pnlTone(n: number | null | undefined): string {
    if (n == null) return 'text-muted-foreground';
    if (n > 0) return 'text-success';
    if (n < 0) return 'text-destructive';
    return 'text-muted-foreground';
  }

  // ----- Mode badges -----
  interface ModeBadge {
    label: string;
    color: string;
    title: string;
    onClick?: () => void;
    // PX5 badge: ícono Zap + pulse animation.
    icon?: 'zap';
    pulse?: boolean;
  }

  let badges = $derived.by((): ModeBadge[] => {
    const out: ModeBadge[] = [];
    if (session.isTimeSlotSession) {
      out.push({
        label: 'TS',
        color: 'bg-primary/20 text-primary border-primary/40',
        title: 'TimeSlots multi-config session',
      });
    }
    if (session.filterWarmupToTradingHours) {
      out.push({
        label: 'WPM',
        color: 'bg-success/20 text-success border-success/40',
        title: 'Warmup-to-trading-hours filter',
      });
    }
    if (session.dwpmActive && session.dwpmLabel) {
      out.push({
        label: `D-WPM:${session.dwpmLabel}`,
        color: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
        title:
          session.dwpmLabel === 'Auto'
            ? 'Deterministic warmup (auto-calculated)'
            : `Deterministic warmup (${session.dwpmLabel} sessions)`,
      });
    }
    if (session.enableBracketReset) {
      out.push({
        label: 'BSR',
        color: 'bg-success/20 text-success border-success/40',
        title: 'Bracket Stop Reset',
      });
    }
    if (session.pollingExit) {
      out.push({
        label: 'PX5',
        // Casa cyan eléctrico, contenido (rayo + letras) en dorado para contraste fuerte.
        color: 'bg-cyan-500/20 text-yellow-400 border-cyan-500/60',
        title:
          'Polling exits (5s intra-candle): bot pide velas cada 5s con trade abierto para detectar stop-loss/trailing antes del cierre del minuto',
        icon: 'zap',
        pulse: true,
      });
    }
    if (session.vNextRestored) {
      out.push({
        label: 'vNext',
        color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
        title: 'vNext warmup restored from snapshot',
      });
    }
    // El badge WP:* y el boton Run Parity ahora viven en <ParityWidget>
    // (Fase 6, render en el header de la card). No los duplicamos aca.
    return out;
  });

  // ----- Config para Strategy Configuration -----
  // Field-level access seguro al objeto config (Record<string, unknown>).
  let cfg = $derived(session.config ?? {});

  function num(v: unknown): number | null {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    return null;
  }
  function str(v: unknown): string | null {
    if (typeof v === 'string' && v.length > 0) return v;
    return null;
  }
  function bool(v: unknown): boolean {
    return v === true;
  }

  // Parameters From: referenceSweepId (preferred) o originSweepId, fallback "Conventional"
  let parametersFromId = $derived.by((): string | null => {
    return str(cfg.referenceSweepId) ?? str(cfg.originSweepId);
  });

  let hasMidCandleExit = $derived(bool(cfg.allowMidCandleExit));

  // ----- Botones de acciones (parity + renko) -----
  // Parity: implementado en Fase 6 via <ParityWidget> debajo.

  // Renko: navega a /charts con la session pre-seleccionada (mismo detail
  // view que cuando entras a Chart Analysis y clickeas una row).
  // El componente ChartAnalysis lee `?s=<sessionId>` y abre el detail.
  function onRenkoClick(e: MouseEvent): void {
    e.stopPropagation();
    push(`/charts?s=${encodeURIComponent(session.sessionId)}`);
  }

  // ----- Helpers visuales -----
  // isDev = modo real (afecta badge "Live"/"Dev" y icon — info de status).
  // useDevLook = apariencia cyan (todos los acentos visuales). Live + toggle
  // "Live looks like Dev" → useDevLook=true sin cambiar el modo real.
  let isDev = $derived(mode === 'developer');
  let useDevLook = $derived(isDev || liveLookStore.liveLooksLikeDev);
  let primaryClass = $derived(useDevLook ? 'text-cyan-300' : 'text-primary');
  let primaryBg = $derived(useDevLook ? 'bg-cyan-500/10' : 'bg-primary/10');
  let primaryBorder = $derived(useDevLook ? 'border-cyan-500/40' : 'border-primary/40');

  let openPosition = $derived(session.openTrades[0] ?? null);

  // ---------------------------------------------------------------
  // Open Position — derived live para el bloque expandido.
  // Reactivos a livePrice (tick WS) y a clock.now (tick 1s).
  // ---------------------------------------------------------------

  // Time-in-trade reactivo: clock.now actualiza cada segundo, recalcula.
  let timeInTrade = $derived.by((): string => {
    if (!openPosition?.entryTime) return '—';
    const entryMs = Date.parse(openPosition.entryTime);
    if (Number.isNaN(entryMs)) return '—';
    const deltaMs = clock.now.getTime() - entryMs;
    if (deltaMs < 0) return '—';
    const totalSec = Math.floor(deltaMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  });

  // Distancia al stop loss en puntos (live). Negativo = livePrice por debajo
  // del SL (peligro para long, ok para short). Color en template.
  // Para long:  livePrice - stopLoss   (> 0 = lejos del SL, seguro)
  // Para short: stopLoss - livePrice   (> 0 = lejos del SL, seguro)
  let distanceToStop = $derived.by((): number | null => {
    if (!openPosition || openPosition.stopLoss == null || livePrice == null) return null;
    if (openPosition.side === 'long') return livePrice - openPosition.stopLoss;
    if (openPosition.side === 'short') return openPosition.stopLoss - livePrice;
    return null;
  });

  // Distancia al take profit en puntos.
  // Para long:  takeProfit - livePrice  (> 0 = aún no se llega, ok)
  // Para short: livePrice - takeProfit
  let distanceToTP = $derived.by((): number | null => {
    if (!openPosition || openPosition.takeProfit == null || livePrice == null) return null;
    if (openPosition.takeProfit === 0) return null;
    if (openPosition.side === 'long') return openPosition.takeProfit - livePrice;
    if (openPosition.side === 'short') return livePrice - openPosition.takeProfit;
    return null;
  });

  // R-multiple unrealized: pnl normalizado por el riesgo inicial (entry - SL).
  // Si no hay SL configurado o el risk es 0, no se calcula.
  let rMultiple = $derived.by((): number | null => {
    if (!openPosition || openPosition.stopLoss == null || openPosition.entryPrice == null) {
      return null;
    }
    const risk = Math.abs(openPosition.entryPrice - openPosition.stopLoss);
    if (risk === 0 || unrealizedTotal == null) return null;
    if (openPosition.valuePerPoint == null || openPosition.quantity == null) return null;
    const riskDollars = risk * openPosition.valuePerPoint * openPosition.quantity;
    if (riskDollars === 0) return null;
    return unrealizedTotal / riskDollars;
  });

  // Copy feedback para orderId.
  let orderIdCopied = $state(false);
  async function copyOrderId(): Promise<void> {
    if (!openPosition?.orderId) return;
    if (await copyText(openPosition.orderId)) {
      flashCopied(
        () => (orderIdCopied = true),
        () => (orderIdCopied = false),
      );
    }
  }

  // Solo HH:mm:ss del entryTime (no incluir fecha, ya está en el header).
  function fmtTimeOnly(t: string | null): string {
    if (!t) return '—';
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  // Distancia en puntos: " 3.25 pt" o "-1.50 pt".
  function fmtPoints(n: number | null): string {
    if (n == null) return '—';
    const sign = n > 0 ? '+' : n < 0 ? '' : ' ';
    return `${sign}${n.toFixed(2)} pt`;
  }

  // R-multiple: "+0.7R" / "-0.5R".
  function fmtR(n: number | null): string {
    if (n == null) return '—';
    const sign = n > 0 ? '+' : n < 0 ? '-' : '';
    return `${sign}${Math.abs(n).toFixed(2)}R`;
  }
</script>

<Card
  class={[
    'transition-all',
    useDevLook && 'border-cyan-500/30',
    expanded && 'shadow-md',
  ]
    .filter(Boolean)
    .join(' ')}
>
  <!-- Header (siempre visible, clickeable). div+role para no anidar buttons. -->
  <div
    role="button"
    tabindex="0"
    class="hover:bg-surface-2 focus-visible:outline-primary flex w-full cursor-pointer flex-col gap-2 rounded-t-lg p-4 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
    onclick={() => onToggle(session.sessionId)}
    onkeydown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle(session.sessionId);
      }
    }}
    aria-expanded={expanded}
  >
    <!-- Row 1: symbol + sessionId (copiable) + daily time + customTitle + status + actions -->
    <div class="flex flex-wrap items-center gap-2">
      <span class={['font-mono text-lg font-semibold', primaryClass].join(' ')}>
        {session.symbol}
      </span>

      <CopyableSessionId sessionId={session.sessionId} />

      {#if session.dailyStartTime || session.dailyEndTime}
        <Tooltip>
          <TooltipTrigger>
            {#snippet child({ props })}
              <span
                {...props}
                class={[
                  'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono text-xs tabular-nums',
                  useDevLook
                    ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                    : 'border-primary/40 bg-primary/10 text-primary',
                ].join(' ')}
              >
                <Clock class="size-3" />
                {session.dailyStartTime ?? '?'}–{session.dailyEndTime ?? '?'}
              </span>
            {/snippet}
          </TooltipTrigger>
          <TooltipContent>Daily trading window (ET)</TooltipContent>
        </Tooltip>
      {/if}

      <!-- Custom title (display + edit) -->
      {#if editingTitle}
        <input
          type="text"
          bind:value={titleDraft}
          onkeydown={onTitleKeydown}
          onclick={(e) => e.stopPropagation()}
          class="border-border bg-surface-2 text-foreground focus:ring-primary/30 focus:border-primary rounded-md border px-2 py-0.5 text-xs italic focus:outline-none focus:ring-2"
          placeholder="Custom title..."
        />
        <button
          type="button"
          class="text-success hover:bg-success/20 rounded p-1"
          aria-label="Save title"
          disabled={savingTitle}
          onclick={(e) => {
            e.stopPropagation();
            void saveTitle();
          }}
        >
          <Check class="size-3.5" />
        </button>
        <button
          type="button"
          class="text-muted-foreground hover:bg-accent rounded p-1"
          aria-label="Cancel"
          onclick={(e) => {
            e.stopPropagation();
            cancelEditTitle();
          }}
        >
          <X class="size-3.5" />
        </button>
      {:else}
        {#if session.customTitle}
          <span class="text-muted-foreground text-xs italic">“{session.customTitle}”</span>
        {/if}
        <button
          type="button"
          class="text-muted-foreground hover:text-foreground hover:bg-accent rounded p-1 opacity-60 transition-opacity hover:opacity-100"
          aria-label="Edit title"
          onclick={(e) => {
            e.stopPropagation();
            startEditTitle();
          }}
        >
          <Pencil class="size-3" />
        </button>
      {/if}

      <!-- Status badge -->
      <div class="ml-auto flex items-center gap-2">
        {#if isDev}
          <Badge class="border-cyan-500/40 bg-cyan-500/15 text-cyan-300 gap-1">
            <Wrench class="size-3" />
            Dev
          </Badge>
        {:else}
          <Badge variant="success" class="animate-live-pulse gap-1">
            <Radio class="size-3" />
            Live
          </Badge>
        {/if}

        <!-- Parity widget: badge WP:* clickeable + boton Run Parity (Fase 6) -->
        <ParityWidget
          sessionId={session.sessionId}
          warmupStatus={parityStatus}
          compact
        />

        <div onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.stopPropagation()} role="presentation">
          <DropdownMenu>
            <DropdownMenuTrigger>
              {#snippet child({ props })}
                <Button
                  {...props}
                  size="icon"
                  variant="ghost"
                  class="text-muted-foreground hover:text-primary size-7"
                  aria-label="More session tools"
                >
                  <MoreHorizontal class="size-3.5" />
                </Button>
              {/snippet}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-52">
              <DropdownMenuLabel>Session tools</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={toolAction !== null}
                onclick={(e) => void copySnapshot(e)}
              >
                {#if toolAction === 'snapshot'}
                  <Loader2 class="size-4 animate-spin" />
                {:else}
                  <ClipboardList class="size-4" />
                {/if}
                Copy Snapshot
                {#if copiedTool === 'snapshot'}
                  <Check class="text-success ml-auto size-3.5" />
                {/if}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={toolAction !== null}
                onclick={(e) => void copyConfig(e)}
              >
                {#if toolAction === 'config'}
                  <Loader2 class="size-4 animate-spin" />
                {:else}
                  <Copy class="size-4" />
                {/if}
                Copy Config
                {#if copiedTool === 'config'}
                  <Check class="text-success ml-auto size-3.5" />
                {/if}
              </DropdownMenuItem>
              <DropdownMenuItem disabled={toolAction !== null} onclick={openCompare}>
                {#if toolAction === 'compare'}
                  <Loader2 class="size-4 animate-spin" />
                {:else}
                  <GitCompare class="size-4" />
                {/if}
                Compare...
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={toolAction !== null}
                onclick={(e) => void openSlippageAudit(e)}
              >
                {#if toolAction === 'slippage'}
                  <Loader2 class="size-4 animate-spin" />
                {:else}
                  <Activity class="size-4" />
                {/if}
                Slippage Audit
              </DropdownMenuItem>
              {#if toolError}
                <DropdownMenuSeparator />
                <DropdownMenuLabel class="text-destructive flex items-center gap-2 text-xs font-normal">
                  <AlertTriangle class="size-3.5" />
                  {toolError}
                </DropdownMenuLabel>
              {/if}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Tooltip>
          <TooltipTrigger>
            {#snippet child({ props })}
              <Button
                {...props}
                size="icon"
                variant="ghost"
                class="text-muted-foreground hover:text-primary size-7"
                aria-label={renkoExists ? 'Open Renko chart' : 'Create Renko blocks'}
                onclick={onRenkoClick}
              >
                {#if renkoExists}
                  <BarChart3 class="size-3.5" />
                {:else}
                  <Hammer class="size-3.5" />
                {/if}
              </Button>
            {/snippet}
          </TooltipTrigger>
          <TooltipContent>
            {renkoExists ? 'Open Renko chart · Phase 5' : 'Create Renko blocks · Phase 5'}
          </TooltipContent>
        </Tooltip>

        <ChevronDown
          class={[
            'text-muted-foreground size-4 transition-transform',
            expanded && 'rotate-180',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      </div>
    </div>

    <!-- Row 2: account + operator + mode badges -->
    <div class="flex flex-wrap items-center gap-1.5 text-xs">
      <span class="text-muted-foreground">{fmtAccount(session.accountName)}</span>
      {#each badges as b (b.label)}
        <span
          class={[
            'inline-flex items-center gap-1 rounded border px-1.5 text-[10px] font-bold tabular-nums tracking-wider',
            b.color,
            b.pulse && 'animate-px5-pulse',
          ]
            .filter(Boolean)
            .join(' ')}
          title={b.title}
        >
          {#if b.icon === 'zap'}
            <Zap class="h-3 w-3 fill-yellow-400 text-yellow-400" strokeWidth={2.5} />
          {/if}
          {b.label}
        </span>
      {/each}
    </div>

    <!-- Row 3: header stats grid (siempre visible) -->
    <div class="mt-1 grid grid-cols-3 gap-x-4 gap-y-2 md:grid-cols-7">
      <!-- P&L (real, fill prices) + BT P&L (sin slippage) stacked -->
      <div>
        <div class="text-muted-foreground text-[10px] uppercase tracking-wider">P&L</div>
        <div
          class={[
            'font-mono text-sm font-semibold tabular-nums',
            session.totalPnL > 0 && 'text-success',
            session.totalPnL < 0 && 'text-destructive',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {fmtMoney(session.totalPnL)}
        </div>
        {#if session.tradeCount > 0}
          <div
            class="text-muted-foreground/70 font-mono text-[10px] tabular-nums"
            title="BT P&L (sin slippage, trigger prices)"
          >
            BT {fmtMoney(session.totalBtPnL)}
          </div>
        {/if}
      </div>

      <!-- Trades -->
      <div>
        <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Trades</div>
        <div class="font-mono text-sm tabular-nums">{session.tradeCount}</div>
      </div>

      <!-- PF -->
      <div>
        <div class="text-muted-foreground text-[10px] uppercase tracking-wider">PF</div>
        <div class="font-mono text-sm tabular-nums">
          {fmtPF(session.profitFactor, session.tradeCount)}
        </div>
      </div>

      <!-- Win Rate -->
      <div>
        <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Win Rate</div>
        <div class="font-mono text-sm tabular-nums">
          {session.tradeCount > 0 ? `${session.winRate.toFixed(1)}%` : '—'}
        </div>
      </div>

      <!-- Timespan / Slots -->
      <div>
        <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
          {session.isTimeSlotSession ? 'Slots' : 'Timespan'}
        </div>
        <div class="font-mono text-sm tabular-nums">
          {#if session.isTimeSlotSession}
            {(session.timeSlots?.length ?? 0)} slots
          {:else}
            {str(cfg.timeSpan) ?? 'N/A'}
          {/if}
        </div>
      </div>

      <!-- Operator -->
      <div>
        <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Operator</div>
        <div class="font-mono text-sm tabular-nums">{cleanOperator(session.operatorId)}</div>
      </div>

      <!-- Equity sparkline -->
      <div class={['col-span-3 md:col-span-1', primaryClass].filter(Boolean).join(' ')}>
        <div class="flex items-baseline gap-2">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Equity</div>
          {#if equityValue != null}
            <div class={['font-mono text-[11px] font-semibold tabular-nums', pnlTone(equityValue)].join(' ')}>
              {fmtEquity(equityValue)}
            </div>
          {/if}
        </div>
        <Sparkline points={session.recentClosedPnL} showValue={false} />
      </div>
    </div>

    <!-- Row 4: open position info (si la hay) -->
    {#if openPosition}
      <div
        class={[
          'mt-1 rounded border border-dashed p-2',
          openPosition.side === 'long'
            ? 'border-success/40 bg-success/5'
            : 'border-destructive/40 bg-destructive/5',
        ].join(' ')}
      >
        <div class="text-muted-foreground mb-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider">
          📍 Open Position
        </div>
        <div class="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
          <!-- Row 1: Side×Qty | Entry | Live Price (pulse) | Live P&L (pulse + R) -->
          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Side × Qty</div>
            <div
              class={[
                'flex items-center gap-1 font-mono text-sm uppercase tabular-nums',
                openPosition.side === 'long' ? 'text-success' : 'text-destructive',
              ].join(' ')}
            >
              {#if openPosition.side === 'long'}
                <TrendingUp class="size-3.5" />
              {:else}
                <TrendingDown class="size-3.5" />
              {/if}
              {openPosition.side ?? '—'} × {openPosition.quantity ?? '—'}
            </div>
          </div>

          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Entry</div>
            <div class="font-mono text-sm tabular-nums">{fmtPrice(openPosition.entryPrice)}</div>
          </div>

          <div>
            <div class="text-muted-foreground inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
              Price <Zap class="size-2.5 fill-yellow-400 text-yellow-400" strokeWidth={2.5} />
            </div>
            <!-- key={livePrice} dispara re-mount + animation tick-flash en cada tick. -->
            {#key livePrice}
              <div class="animate-tick-flash inline-block font-mono text-sm tabular-nums">
                {fmtPrice(livePrice)}
              </div>
            {/key}
          </div>

          <div>
            <div class="text-muted-foreground inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
              Live P&L <Zap class="size-2.5 fill-yellow-400 text-yellow-400" strokeWidth={2.5} />
            </div>
            {#key unrealizedTotal}
              <div
                class={[
                  'animate-tick-flash flex items-baseline gap-1.5 font-mono text-sm font-semibold tabular-nums',
                  unrealizedTotal != null && unrealizedTotal > 0 && 'text-success',
                  unrealizedTotal != null && unrealizedTotal < 0 && 'text-destructive',
                  // Etiqueta de estado (waiting/no-vpp/...): tono atenuado
                  // para no competir visualmente con un P&L real. no-vpp en
                  // warning porque es un dato faltante que requiere accion.
                  unrealizedTotal == null && unrealizedReason === 'no-vpp' && 'text-warning',
                  unrealizedTotal == null && unrealizedReason !== 'no-vpp' && 'text-muted-foreground',
                ]
                  .filter(Boolean)
                  .join(' ')}
                title={unrealizedLabelTitle}
              >
                {#if unrealizedTotal != null}
                  {fmtPnL(unrealizedTotal)}
                  {#if rMultiple != null}
                    <span class="text-muted-foreground text-[10px] font-normal">
                      ({fmtR(rMultiple)})
                    </span>
                  {/if}
                {:else}
                  <span class="text-xs font-normal not-italic">{unrealizedLabel}</span>
                {/if}
              </div>
            {/key}
          </div>

          <!-- Row 2: Opened | In Trade | Stop (con distance) | Take Profit (con distance) -->
          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Opened</div>
            <div class="font-mono text-sm tabular-nums">{fmtTimeOnly(openPosition.entryTime)}</div>
          </div>

          <div>
            <div class="text-muted-foreground inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
              <Clock class="size-2.5" /> In Trade
            </div>
            <div class="font-mono text-sm tabular-nums">{timeInTrade}</div>
          </div>

          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Stop</div>
            <div class="flex items-baseline gap-1.5">
              <span class="font-mono text-sm tabular-nums">{fmtPrice(openPosition.stopLoss)}</span>
              {#if distanceToStop != null}
                <!-- distanceToStop > 0: lejos del SL (seguro). < 0: pasado el SL. -->
                <span
                  class={[
                    'font-mono text-[10px] tabular-nums',
                    distanceToStop > 0 && 'text-muted-foreground',
                    distanceToStop <= 0 && 'text-destructive',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {fmtPoints(distanceToStop)}
                </span>
              {/if}
            </div>
          </div>

          <div>
            <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Take Profit</div>
            <div class="flex items-baseline gap-1.5">
              <span class="font-mono text-sm tabular-nums">{fmtPrice(openPosition.takeProfit)}</span>
              {#if distanceToTP != null}
                <!-- distanceToTP > 0: aún no se llega (neutral). <= 0: alcanzado (success). -->
                <span
                  class={[
                    'font-mono text-[10px] tabular-nums',
                    distanceToTP > 0 && 'text-muted-foreground',
                    distanceToTP <= 0 && 'text-success',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {fmtPoints(distanceToTP)}
                </span>
              {/if}
            </div>
          </div>
        </div>

        <!-- OrderId copiable (sub-row separada, font chico, no compite con el grid). -->
        {#if openPosition.orderId}
          <div class="border-border/40 mt-2 flex items-center gap-1 border-t pt-1.5">
            <span class="text-muted-foreground text-[10px] uppercase tracking-wider">Order</span>
            <button
              type="button"
              class="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-mono text-[11px] tabular-nums transition-colors"
              onclick={(e) => {
                e.stopPropagation();
                void copyOrderId();
              }}
              title="Click to copy orderId"
            >
              {openPosition.orderId}
              {#if orderIdCopied}
                <Check class="text-success size-3" />
              {:else}
                <Copy class="size-3" />
              {/if}
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Body colapsable: delega al SessionDetailPanel compartido -->
  {#if expanded}
    <SessionDetailPanel
      tradeCount={session.tradeCount}
      totalPnL={session.totalPnL}
      totalBtPnL={session.totalBtPnL}
      profitFactor={session.profitFactor}
      winRate={session.winRate}
      maxDrawdown={session.maxDrawdown}
      config={session.config}
      openTrades={session.openTrades}
      closedTrades={session.closedTrades}
      theme={useDevLook ? 'developer' : 'live'}
    />
  {/if}
</Card>

<Dialog
  bind:open={slippageOpen}
  onOpenChange={(open) => {
    if (!open) slippageOpen = false;
  }}
>
  <DialogContent class="max-h-[88vh] max-w-6xl overflow-y-auto">
    <div class="flex flex-wrap items-center justify-between gap-3 pr-8">
      <DialogTitle class="flex flex-wrap items-center gap-2.5">
        <Activity class={['size-5', primaryClass].join(' ')} />
        <span class={['text-display text-2xl', primaryClass].join(' ')}>Slippage Audit</span>
        <CopyableSessionId sessionId={session.sessionId} stopPropagation={false} />
      </DialogTitle>
      <Button
        variant="outline"
        size="sm"
        disabled={!slippageAudit}
        onclick={() => void copyCurrentAudit()}
      >
        {#if copiedTool === 'audit'}
          <Check class="text-success size-3.5" />
          Copied
        {:else}
          <Copy class="size-3.5" />
          Copy
        {/if}
      </Button>
    </div>
    <DialogDescription class="sr-only">
      Read-only comparison of real fill P&L against BT-style trigger P&L.
    </DialogDescription>

    {#if slippageAudit}
      <div class="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Real P&L</div>
          <div class={['font-mono text-lg font-semibold tabular-nums', pnlTone(slippageAudit.totalRealPnL)].join(' ')}>
            {fmtMoney(slippageAudit.totalRealPnL)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">BT P&L</div>
          <div class={['font-mono text-lg font-semibold tabular-nums', pnlTone(slippageAudit.totalBtPnL)].join(' ')}>
            {fmtMoney(slippageAudit.totalBtPnL)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Diff</div>
          <div class={['font-mono text-lg font-semibold tabular-nums', pnlTone(slippageAudit.pnlDiff)].join(' ')}>
            {fmtMoney(slippageAudit.pnlDiff)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Trades</div>
          <div class="font-mono text-lg font-semibold tabular-nums">{slippageAudit.tradeCount}</div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Comparable</div>
          <div class="font-mono text-lg font-semibold tabular-nums">
            {slippageAudit.comparableTradeCount}
          </div>
        </div>
      </div>

      {#if slippageAudit.warnings.length > 0}
        <div
          role="status"
          class="border-warning/40 bg-warning/10 text-warning mt-4 rounded-md border p-3 text-sm"
        >
          <div class="mb-1 flex items-center gap-2 font-medium">
            <AlertTriangle class="size-4" />
            Data quality
          </div>
          <ul class="text-muted-foreground list-disc space-y-1 pl-5">
            {#each slippageAudit.warnings as warning (warning)}
              <li>{warning}</li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if slippageAudit.largestDiffs.length > 0}
        <div class="text-muted-foreground mt-4 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>Trades by P&L diff</span>
          <span class="font-mono tabular-nums">
            {slippageAudit.largestDiffs.length} of {slippageAudit.comparableTradeCount} comparable
          </span>
        </div>
        <div class="mt-2 overflow-x-auto rounded-md border">
          <table class="w-full text-xs">
            <thead class="bg-muted/40 text-muted-foreground">
              <tr>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Entry</th>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Side</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Qty</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Real</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">BT</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Diff</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Fill</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider">Trigger</th>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Reason</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              {#each slippageAudit.largestDiffs as t, i (`${t.entryTime ?? i}-${t.diff ?? 0}`)}
                <tr class="hover:bg-muted/20">
                  <td class="text-muted-foreground px-3 py-2 font-mono tabular-nums">
                    {fmtDateTime(t.entryTime ?? null)}
                  </td>
                  <td class="px-3 py-2 font-mono uppercase">{fmtField(t.side)}</td>
                  <td class="px-3 py-2 text-right font-mono tabular-nums">
                    {fmtField(t.quantity)}
                  </td>
                  <td class={['px-3 py-2 text-right font-mono tabular-nums', pnlTone(t.realPnL)].join(' ')}>
                    {fmtMoney(t.realPnL ?? null)}
                  </td>
                  <td class={['px-3 py-2 text-right font-mono tabular-nums', pnlTone(t.btPnL)].join(' ')}>
                    {fmtMoney(t.btPnL ?? null)}
                  </td>
                  <td class={['px-3 py-2 text-right font-mono font-semibold tabular-nums', pnlTone(t.diff)].join(' ')}>
                    {fmtMoney(t.diff ?? null)}
                  </td>
                  <td class="px-3 py-2 text-right font-mono tabular-nums">
                    {fmtPrice(t.entryPrice ?? null)}
                  </td>
                  <td class="px-3 py-2 text-right font-mono tabular-nums">
                    {fmtPrice(t.entryTriggerPrice ?? null)}
                  </td>
                  <td class="text-muted-foreground px-3 py-2">{fmtField(t.closeReason)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <div class="text-muted-foreground mt-4 rounded-md border p-6 text-center text-sm">
          No comparable closed trades for this audit yet.
        </div>
      {/if}
    {:else}
      <div class="text-muted-foreground mt-8 flex items-center justify-center gap-3 py-12">
        <Loader2 class="size-5 animate-spin" />
        <span class="text-sm">Loading audit...</span>
      </div>
    {/if}
  </DialogContent>
</Dialog>

<Dialog
  bind:open={compareOpen}
  onOpenChange={(open) => {
    if (!open) compareOpen = false;
  }}
>
  <DialogContent class="max-h-[88vh] max-w-6xl overflow-y-auto">
    <div class="flex flex-wrap items-center justify-between gap-3 pr-8">
      <DialogTitle class="flex flex-wrap items-center gap-2.5">
        <GitCompare class={['size-5', primaryClass].join(' ')} />
        <span class={['text-display text-2xl', primaryClass].join(' ')}>Compare Session</span>
        <CopyableSessionId sessionId={session.sessionId} stopPropagation={false} />
      </DialogTitle>
      <Button
        variant="outline"
        size="sm"
        disabled={!compareResult}
        onclick={() => void copyCurrentCompare()}
      >
        {#if copiedTool === 'compare'}
          <Check class="text-success size-3.5" />
          Copied
        {:else}
          <Copy class="size-3.5" />
          Copy
        {/if}
      </Button>
    </div>
    <DialogDescription class="sr-only">
      Read-only comparison of session metrics and safe strategy config.
    </DialogDescription>

    <form class="mt-5 flex flex-col gap-2 lg:flex-row" onsubmit={(e) => void runCompare(e)}>
      <div class="min-w-0 flex-1">
        <DropdownMenu>
          <DropdownMenuTrigger>
            {#snippet child({ props })}
              <Button
                {...props}
                type="button"
                variant="outline"
                class="h-auto min-h-14 w-full justify-between gap-3 px-3 py-2 text-left"
                disabled={compareCandidatesForMenu.length === 0}
              >
                {#if selectedCompareCandidate}
                  <span class="grid min-w-0 flex-1 grid-cols-[72px_126px_minmax(140px,1fr)_78px_76px] items-center gap-x-3 gap-y-1">
                    <span class="font-mono text-sm font-semibold">
                      {selectedCompareCandidate.symbol}
                    </span>
                    <span class="bg-muted/50 text-muted-foreground inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[11px] tabular-nums">
                      <Clock class="size-3" />
                      {sessionWindowLabel(selectedCompareCandidate)}
                    </span>
                    <span class="min-w-0 truncate font-mono text-xs" title={selectedCompareCandidate.sessionId}>
                      {selectedCompareCandidate.sessionId}
                    </span>
                    <span class={['text-right font-mono text-xs tabular-nums', pnlTone(selectedCompareCandidate.totalPnL)].join(' ')}>
                      {fmtMoney(selectedCompareCandidate.totalPnL)}
                    </span>
                    <span class="text-muted-foreground text-right font-mono text-[11px] tabular-nums">
                      {selectedCompareCandidate.tradeCount} trades
                    </span>
                    <span class="text-muted-foreground col-span-5 grid grid-cols-[72px_126px_minmax(140px,1fr)_78px_76px] gap-x-3 text-[11px]">
                      <span>{cleanOperator(selectedCompareCandidate.operatorId)}</span>
                      <span>{sessionTimespanLabel(selectedCompareCandidate)}</span>
                      <span class="min-w-0 truncate">{fmtField(selectedCompareCandidate.accountName)}</span>
                      <span class="text-right">Start</span>
                      <span class="truncate text-right" title={sessionStartLabel(selectedCompareCandidate)}>
                        {sessionStartLabel(selectedCompareCandidate)}
                      </span>
                    </span>
                  </span>
                {:else}
                  <span class="text-muted-foreground">
                    {compareCandidatesForMenu.length === 0
                      ? 'No other live sessions'
                      : 'Select live session'}
                  </span>
                {/if}
                <ChevronDown class="text-muted-foreground size-4 shrink-0" />
              </Button>
            {/snippet}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            class="max-h-[55vh] w-[820px] max-w-[calc(100vw-2rem)] overflow-y-auto p-1"
          >
            <DropdownMenuLabel>Live sessions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {#if compareCandidatesForMenu.length === 0}
              <div class="text-muted-foreground px-2 py-6 text-center text-sm">
                No other sessions are available in this monitor.
              </div>
            {:else}
              <div class="text-muted-foreground grid grid-cols-[72px_126px_minmax(150px,1fr)_82px_78px_90px] gap-x-3 px-2 py-1.5 text-[10px] uppercase tracking-wider">
                <span>Symbol</span>
                <span>Window</span>
                <span>Session</span>
                <span class="text-right">P&L</span>
                <span class="text-right">Trades</span>
                <span class="text-right">Meta</span>
              </div>
              {#each compareCandidatesForMenu as candidate (candidate.sessionId)}
                <DropdownMenuItem
                  class="grid grid-cols-[72px_126px_minmax(150px,1fr)_82px_78px_90px_18px] items-center gap-x-3 gap-y-1 rounded-md py-2"
                  onclick={() => selectCompareCandidate(candidate)}
                >
                  <span class="font-mono text-sm font-semibold">{candidate.symbol}</span>
                  <span class="border-border/60 bg-muted/40 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[11px] tabular-nums">
                    <Clock class="size-3" />
                    {sessionWindowLabel(candidate)}
                  </span>
                  <span class="min-w-0 truncate font-mono text-xs" title={candidate.sessionId}>
                    {candidate.sessionId}
                  </span>
                  <span class={['text-right font-mono text-xs font-semibold tabular-nums', pnlTone(candidate.totalPnL)].join(' ')}>
                    {fmtMoney(candidate.totalPnL)}
                  </span>
                  <span class="text-muted-foreground text-right font-mono text-[11px] tabular-nums">
                    {candidate.tradeCount}
                  </span>
                  <span class="text-muted-foreground text-right font-mono text-[11px] tabular-nums">
                    {sessionTimespanLabel(candidate)}
                  </span>
                  <span>
                    {#if compareTarget === candidate.sessionId}
                      <Check class="text-success size-3.5 shrink-0" />
                    {/if}
                  </span>
                  <span class="text-muted-foreground col-span-7 grid grid-cols-[72px_126px_minmax(150px,1fr)_82px_78px_90px_18px] gap-x-3 text-[11px]">
                    <span>{cleanOperator(candidate.operatorId)}</span>
                    <span>{fmtField(candidate.accountName)}</span>
                    <span class="min-w-0 truncate" title={sessionStartLabel(candidate)}>
                      Start {sessionStartLabel(candidate)}
                    </span>
                    <span class="text-right">BT</span>
                    <span class="text-right font-mono tabular-nums">{fmtMoney(candidate.totalBtPnL)}</span>
                    <span class="text-right">Open {candidate.openPositions}</span>
                    <span></span>
                  </span>
                </DropdownMenuItem>
              {/each}
            {/if}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Button type="submit" disabled={toolAction === 'compare' || !compareTarget.trim()}>
        {#if toolAction === 'compare'}
          <Loader2 class="size-3.5 animate-spin" />
          Comparing
        {:else}
          <GitCompare class="size-3.5" />
          Compare
        {/if}
      </Button>
    </form>

    {#if compareError}
      <div
        role="alert"
        class="border-destructive/40 bg-destructive/10 text-destructive mt-4 rounded-md border p-3 text-sm"
      >
        <div class="flex items-center gap-2 font-medium">
          <AlertTriangle class="size-4" />
          {compareError}
        </div>
        <div class="text-muted-foreground mt-1">
          Check the target sessionId and try again.
        </div>
      </div>
    {/if}

    {#if compareResult}
      {@const configDiffs = changedConfigDiffs(compareResult)}
      <div class="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Real P&L diff</div>
          <div class={['font-mono text-lg font-semibold tabular-nums', pnlTone(compareResult.summaryDiff.totalPnLDiff)].join(' ')}>
            {fmtMoney(compareResult.summaryDiff.totalPnLDiff)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">BT P&L diff</div>
          <div class={['font-mono text-lg font-semibold tabular-nums', pnlTone(compareResult.summaryDiff.totalBtPnLDiff)].join(' ')}>
            {fmtMoney(compareResult.summaryDiff.totalBtPnLDiff)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Trades diff</div>
          <div class="font-mono text-lg font-semibold tabular-nums">
            {fmtDiff(compareResult.summaryDiff.tradeCountDiff)}
          </div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Config changes</div>
          <div class="font-mono text-lg font-semibold tabular-nums">{configDiffs.length}</div>
        </div>
        <div class="bg-muted/30 rounded-md border p-3">
          <div class="text-muted-foreground text-[10px] uppercase tracking-wider">Trade pairs</div>
          <div class="font-mono text-lg font-semibold tabular-nums">
            {compareResult.tradePairs.length}
          </div>
        </div>
      </div>

      <div class="mt-4 grid gap-3 md:grid-cols-2">
        <div class="bg-muted/20 rounded-md border p-3">
          <div class="text-muted-foreground mb-2 text-[10px] uppercase tracking-wider">Base</div>
          <div class="flex flex-wrap items-center gap-2">
            <CopyableSessionId sessionId={compareResult.base.sessionId} stopPropagation={false} />
            <span class="font-mono text-sm">{fmtField(compareResult.base.symbol)}</span>
            <span class="text-muted-foreground text-xs">{fmtField(compareResult.base.accountName)}</span>
          </div>
          <div class="mt-2 grid grid-cols-3 gap-2 text-xs">
            <span>P&L <b class={pnlTone(compareResult.base.totalPnL)}>{fmtMoney(compareResult.base.totalPnL)}</b></span>
            <span>Trades <b>{compareResult.base.tradeCount}</b></span>
            <span>Parity <b>{fmtField(compareResult.base.parityStatus)}</b></span>
          </div>
        </div>
        <div class="bg-muted/20 rounded-md border p-3">
          <div class="text-muted-foreground mb-2 text-[10px] uppercase tracking-wider">Other</div>
          <div class="flex flex-wrap items-center gap-2">
            <CopyableSessionId sessionId={compareResult.other.sessionId} stopPropagation={false} />
            <span class="font-mono text-sm">{fmtField(compareResult.other.symbol)}</span>
            <span class="text-muted-foreground text-xs">{fmtField(compareResult.other.accountName)}</span>
          </div>
          <div class="mt-2 grid grid-cols-3 gap-2 text-xs">
            <span>P&L <b class={pnlTone(compareResult.other.totalPnL)}>{fmtMoney(compareResult.other.totalPnL)}</b></span>
            <span>Trades <b>{compareResult.other.tradeCount}</b></span>
            <span>Parity <b>{fmtField(compareResult.other.parityStatus)}</b></span>
          </div>
        </div>
      </div>

      {#if compareResult.warnings.length > 0}
        <div
          role="status"
          class="border-warning/40 bg-warning/10 text-warning mt-4 rounded-md border p-3 text-sm"
        >
          <div class="mb-1 flex items-center gap-2 font-medium">
            <AlertTriangle class="size-4" />
            Data quality
          </div>
          <ul class="text-muted-foreground list-disc space-y-1 pl-5">
            {#each compareResult.warnings as warning (warning)}
              <li>{warning}</li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if compareResult.tradePairs.length > 0}
        <div class="text-muted-foreground mt-4 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>Trades side by side</span>
          <span class="font-mono tabular-nums">
            {compareResult.pairing.matched} paired by time / {compareResult.pairing.matchedByIndex} by order / {compareResult.pairing.baseOnly} base only / {compareResult.pairing.otherOnly} other only
          </span>
        </div>
        <div class="mt-2 overflow-x-auto rounded-md border">
          <table class="w-full min-w-[980px] text-xs">
            <thead class="bg-muted/40">
              <tr>
                <th class="text-muted-foreground px-3 py-2 text-left font-normal uppercase tracking-wider">
                  #
                </th>
                <th class="text-primary px-3 py-2 text-left font-medium uppercase tracking-wider">
                  <span class="flex flex-col gap-0.5">
                    <span>Base trade</span>
                    <span class="text-primary/70 font-mono text-[10px] normal-case tracking-normal">
                      {shortSessionId(compareResult.base.sessionId)}
                    </span>
                  </span>
                </th>
                <th class="text-warning px-3 py-2 text-left font-medium uppercase tracking-wider">
                  <span class="flex flex-col gap-0.5">
                    <span>Other trade</span>
                    <span class="text-warning/75 font-mono text-[10px] normal-case tracking-normal">
                      {shortSessionId(compareResult.other.sessionId)}
                    </span>
                  </span>
                </th>
                <th class="text-success/90 px-3 py-2 text-right font-medium uppercase tracking-wider">
                  Real diff
                </th>
                <th class="text-gold/80 px-3 py-2 text-right font-medium uppercase tracking-wider">
                  BT diff
                </th>
                <th class="px-3 py-2 text-right font-medium uppercase tracking-wider text-cyan-300/90">
                  Entry delta
                </th>
                <th class="text-muted-foreground px-3 py-2 text-right font-normal uppercase tracking-wider">
                  More
                </th>
              </tr>
            </thead>
            <tbody class="divide-y">
              {#each compareResult.tradePairs as pair (pair.index)}
                {@const expandedTrade = expandedCompareTradeIndexes.has(pair.index)}
                <tr class="hover:bg-muted/20">
                  <td class={['px-3 py-2 align-top font-mono tabular-nums', tradeMismatchTone(pair)].join(' ')}>
                    <div>#{pair.index}</div>
                    <div class="mt-0.5 text-[10px] uppercase tracking-wider">
                      {fmtPairStatus(pair)}
                    </div>
                  </td>
                  <td class="px-3 py-2 align-top">
                    <div class={['font-mono text-xs font-semibold uppercase', !pair.base && 'text-warning'].filter(Boolean).join(' ')}>
                      {tradeHeadline(pair.base)}
                    </div>
                    <div class="text-muted-foreground mt-0.5 font-mono text-[11px] tabular-nums">
                      {tradeSubline(pair.base)}
                    </div>
                    <div class="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2 font-mono text-[10px] tabular-nums">
                      <span title={pair.base?.tradeId ?? undefined}>
                        {shortValue(pair.base?.tradeId ?? pair.base?.orderId ?? null)}
                      </span>
                      <span>{renkoShortLabel(pair.base?.entryRenko ?? null)}</span>
                    </div>
                    <div class="mt-1 flex flex-wrap items-center gap-2">
                      <span class={['font-mono font-semibold tabular-nums', pnlTone(pair.base?.pnl ?? null)].join(' ')}>
                        {fmtMoney(pair.base?.pnl ?? null)}
                      </span>
                      <span class="text-muted-foreground font-mono text-[11px] tabular-nums">
                        BT {fmtMoney(pair.base?.btPnl ?? null)}
                      </span>
                    </div>
                  </td>
                  <td class="px-3 py-2 align-top">
                    <div class={['font-mono text-xs font-semibold uppercase', !pair.other && 'text-warning'].filter(Boolean).join(' ')}>
                      {tradeHeadline(pair.other)}
                    </div>
                    <div class="text-muted-foreground mt-0.5 font-mono text-[11px] tabular-nums">
                      {tradeSubline(pair.other)}
                    </div>
                    <div class="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2 font-mono text-[10px] tabular-nums">
                      <span title={pair.other?.tradeId ?? undefined}>
                        {shortValue(pair.other?.tradeId ?? pair.other?.orderId ?? null)}
                      </span>
                      <span>{renkoShortLabel(pair.other?.entryRenko ?? null)}</span>
                    </div>
                    <div class="mt-1 flex flex-wrap items-center gap-2">
                      <span class={['font-mono font-semibold tabular-nums', pnlTone(pair.other?.pnl ?? null)].join(' ')}>
                        {fmtMoney(pair.other?.pnl ?? null)}
                      </span>
                      <span class="text-muted-foreground font-mono text-[11px] tabular-nums">
                        BT {fmtMoney(pair.other?.btPnl ?? null)}
                      </span>
                    </div>
                  </td>
                  <td class={['px-3 py-2 text-right font-mono font-semibold tabular-nums', pnlTone(pair.pnlDiff)].join(' ')}>
                    {fmtMoney(pair.pnlDiff)}
                  </td>
                  <td class={['px-3 py-2 text-right font-mono tabular-nums', pnlTone(pair.btPnlDiff)].join(' ')}>
                    {fmtMoney(pair.btPnlDiff)}
                  </td>
                  <td class="text-muted-foreground px-3 py-2 text-right font-mono tabular-nums">
                    <div>{fmtTimeDiff(pair.entryTimeDiffMs)}</div>
                    <div class="mt-0.5 text-[10px]">
                      ctx {fmtDiff(pair.entryRenkoIndexDiff)}
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      class="size-7"
                      aria-label={expandedTrade ? 'Collapse trade details' : 'Expand trade details'}
                      onclick={() => toggleCompareTrade(pair.index)}
                    >
                      <ChevronDown
                        class={[
                          'size-3.5 transition-transform',
                          expandedTrade && 'rotate-180',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      />
                    </Button>
                  </td>
                </tr>
                {#if expandedTrade}
                  <tr class="bg-muted/10">
                    <td colspan="7" class="px-3 py-3">
                      <div class="grid gap-3 md:grid-cols-2">
                        <div class="rounded-md border bg-background/50 p-3">
                          <div class="text-primary mb-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider">
                            <span>Base details</span>
                            <span class="text-primary/70 font-mono normal-case tracking-normal">
                              {shortSessionId(compareResult.base.sessionId)}
                            </span>
                          </div>
                          <dl class="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
                            {#each tradeDetailRows(pair.base) as row (row[0])}
                              <dt class="text-muted-foreground">{row[0]}</dt>
                              <dd class="break-words font-mono tabular-nums">{row[1]}</dd>
                            {/each}
                          </dl>
                        </div>
                        <div class="rounded-md border bg-background/50 p-3">
                          <div class="text-warning mb-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider">
                            <span>Other details</span>
                            <span class="text-warning/75 font-mono normal-case tracking-normal">
                              {shortSessionId(compareResult.other.sessionId)}
                            </span>
                          </div>
                          <dl class="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
                            {#each tradeDetailRows(pair.other) as row (row[0])}
                              <dt class="text-muted-foreground">{row[0]}</dt>
                              <dd class="break-words font-mono tabular-nums">{row[1]}</dd>
                            {/each}
                          </dl>
                        </div>
                      </div>
                    </td>
                  </tr>
                {/if}
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <div class="text-muted-foreground mt-4 rounded-md border p-6 text-center text-sm">
          No trades found for these sessions.
        </div>
      {/if}

      {#if configDiffs.length > 0}
        <div class="text-muted-foreground mt-4 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>Changed config</span>
          <span class="font-mono tabular-nums">{configDiffs.length} keys</span>
        </div>
        <div class="mt-2 overflow-x-auto rounded-md border">
          <table class="w-full text-xs">
            <thead class="bg-muted/40 text-muted-foreground">
              <tr>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Key</th>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Base</th>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider">Other</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              {#each configDiffs as diff (diff.key)}
                <tr class="hover:bg-muted/20">
                  <td class="px-3 py-2 font-mono font-medium">{diff.key}</td>
                  <td class="px-3 py-2 font-mono tabular-nums">{fmtConfigValue(diff.base)}</td>
                  <td class="px-3 py-2 font-mono tabular-nums">{fmtConfigValue(diff.other)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <div class="text-muted-foreground mt-4 rounded-md border p-6 text-center text-sm">
          Safe config matches for these sessions.
        </div>
      {/if}
    {/if}
  </DialogContent>
</Dialog>
